/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import makeStyles from '@material-ui/core/styles/makeStyles';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { format } from 'd3-format';
import moment from 'moment';
import React from 'react';

const useStyles = makeStyles(() => ({
  root: {
    maxWidth: 400, // Increase if needed
    minWidth: 200,
    maxHeight: 400, // Adjust as needed
    // overflow: 'hidden', // avoid double scrollbars
    padding: 16, // theme.spacing(2),
  },
  layerSelect: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
  },
  tableContainer: {
    //// DEBUG
    // border: '1px solid red',
    // backgroundColor: '#0f0f0f',
    maxHeight: 300,
    overflowY: 'auto',
    scrollbarColor: 'initial !important', // or 'auto auto !important'
    scrollbarWidth: 'auto',
    '&::-webkit-scrollbar': {
      width: 16, // Set your desired width here (e.g., 16px)
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#bdbdbd',
      borderRadius: 8,
    },
  },
  table: {
    tableLayout: 'fixed',
    width: '100%',
    '& *': {
      wordBreak: 'break-word',
      whiteSpace: 'normal',
    },
    '& tr': {
      '&:last-child td': {
        borderBottomColor: 'transparent',
      },
      '& td': {
        verticalAlign: 'baseline',
        '&:nth-child(1)': {
          width: '45%',
          maxWidth: 200,
          '& p': {
            fontWeight: 500,
          },
        },
        '&:nth-child(2)': {
          width: '55%',
          maxWidth: 300,
        },
      },
    },
  },
  title: {
    position: 'relative',
    fontSize: '1rem', // or '0.9rem', '14px', etc.
  },
}));

interface ILayerPopupComponent {
  activeInteractiveLayer: any;
  activeInteractiveLayers: any;
}

function formatColumnName(key: string) {
  // Insert a space before all caps and capitalize the first letter
  return (
    key
      // .replace(/([a-z])([A-Z])/g, '$1 $2')
      // .replace(/^./, (str) => str.toUpperCase());
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
}

const LayerPopupComponent = (props: ILayerPopupComponent) => {
  const { activeInteractiveLayer } = props;
  const classes = useStyles();

  const { name, data } = activeInteractiveLayer;
  const { interactionConfig } = activeInteractiveLayer.config || activeInteractiveLayer;
  const { output = interactionConfig?.tooltip ?? [] } = interactionConfig || {};

  // Print output to the console for debugging
  console.log('LayerPopupComponent name:', name);
  console.log('LayerPopupComponent data:', data);
  // console.log('LayerPopupComponent interactionConfig:', interactionConfig);
  // console.log('LayerPopupComponent output:', output);

  return (
    <div className={classes.root}>
      <Typography variant="h6" className={classes.title} paragraph={true}>
        {name}
      </Typography>

      <TableContainer className={classes.tableContainer} component={Paper}>
        <Table className={classes.table} size="small">
          <TableBody>
            {Object.entries(data?.data || data || {})
              //.filter(([key]) => !key.startsWith('OSM_'))
              .map(([key, value]) => (
                <TableRow key={key}>
                  <TableCell align="right">
                    <Typography variant="body2">{formatColumnName(key)}:</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{String(value)}</Typography>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default LayerPopupComponent;
